import { BulkImportRow, CellRange } from './types';

export interface GridState { columns: string[]; rows: BulkImportRow[]; past: BulkImportRow[][]; future: BulkImportRow[][] }
export type GridAction =
  | { type: 'edit'; row: number; field: string; value: string }
  | { type: 'paste'; anchor: [number, number]; text: string }
  | { type: 'clear'; range: CellRange }
  | { type: 'fillDown'; range: CellRange }
  | { type: 'insert'; row: number }
  | { type: 'duplicate'; row: number }
  | { type: 'delete'; rows: number[] }
  | { type: 'move'; from: number; to: number }
  | { type: 'undo' } | { type: 'redo' };

let rowSequence = 0;
const blank = (columns: string[]): BulkImportRow => Object.assign({ row_id: `draft-${++rowSequence}` }, Object.fromEntries(columns.map(column => [column, ''])));
const cloneRows = (rows: BulkImportRow[]) => rows.map(row => ({ ...row }));
const nonBlank = (row: BulkImportRow, columns: string[]) => columns.some(column => String(row[column] ?? '').trim());
const withHistory = (state: GridState, rows: BulkImportRow[]): GridState => ({ ...state, rows, past: [...state.past, cloneRows(state.rows)].slice(-50), future: [] });

export function initialGrid(columns: string[], count = 1, rows?: BulkImportRow[]): GridState {
  return { columns, rows: rows ? cloneRows(rows) : Array.from({ length: count }, () => blank(columns)), past: [], future: [] };
}

export function reduceGrid(state: GridState, action: GridAction): GridState {
  if (action.type === 'undo') {
    const previous = state.past[state.past.length - 1];
    return previous ? { ...state, rows: cloneRows(previous), past: state.past.slice(0, -1), future: [cloneRows(state.rows), ...state.future] } : state;
  }
  if (action.type === 'redo') {
    const next = state.future[0];
    return next ? { ...state, rows: cloneRows(next), past: [...state.past, cloneRows(state.rows)], future: state.future.slice(1) } : state;
  }
  const rows = cloneRows(state.rows);
  if (action.type === 'edit') {
    if (!rows[action.row]) rows[action.row] = blank(state.columns);
    rows[action.row][action.field] = action.value;
    if (action.row === rows.length - 1 && nonBlank(rows[action.row], state.columns)) rows.push(blank(state.columns));
  } else if (action.type === 'paste') {
    const parsed = action.text.replace(/\r/g, '').split('\n').filter((line, index, all) => line.length || index < all.length - 1)
      .map(line => line.includes('\t') ? line.split('\t') : line.split(','));
    parsed.forEach((values, rowOffset) => {
      const rowIndex = action.anchor[0] + rowOffset;
      while (!rows[rowIndex]) rows.push(blank(state.columns));
      values.forEach((value, columnOffset) => {
        const field = state.columns[action.anchor[1] + columnOffset];
        if (field) rows[rowIndex][field] = value;
      });
    });
    if (rows.length && nonBlank(rows[rows.length - 1], state.columns)) rows.push(blank(state.columns));
  } else if (action.type === 'clear' || action.type === 'fillDown') {
    const [startRow, startColumn] = action.range.start;
    const [endRow, endColumn] = action.range.end;
    for (let row = Math.min(startRow, endRow); row <= Math.max(startRow, endRow); row += 1) {
      for (let column = Math.min(startColumn, endColumn); column <= Math.max(startColumn, endColumn); column += 1) {
        if (rows[row] && state.columns[column]) rows[row][state.columns[column]] = action.type === 'clear' ? '' : rows[startRow]?.[state.columns[column]] ?? '';
      }
    }
  } else if (action.type === 'insert') rows.splice(action.row, 0, blank(state.columns));
  else if (action.type === 'duplicate' && rows[action.row]) rows.splice(action.row + 1, 0, { ...rows[action.row], row_id: `draft-${++rowSequence}` });
  else if (action.type === 'delete') {
    const deleted = new Set(action.rows); const remaining = rows.filter((_row, index) => !deleted.has(index));
    rows.splice(0, rows.length, ...(remaining.length ? remaining : [blank(state.columns)]));
  } else if (action.type === 'move' && rows[action.from]) {
    const [moved] = rows.splice(action.from, 1); rows.splice(action.to, 0, moved);
  }
  return withHistory(state, rows);
}
